import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop';

import type { UserUpdateInfoData } from '../ContentStateInfoPopup/UserUpdateInfo.tsx';

import type {
	useUserProfileDataQuery$data,
	useUserProfileDataQuery$variables,
} from './__generated__/useUserProfileDataQuery.graphql.ts';

type UseUserProfileDataReturn = {
	updatedBy?: UserUpdateInfoData;
	loading: boolean;
};

/**
 * Hook to fetch user profile data by account ID (AAID).
 * Returns user display name, avatar URL, and account ID for use in ContentStateInfoPopup.
 *
 * @param accountId - The account ID (AAID) to fetch user data for, or null/undefined to skip the query
 * @returns Object with updatedBy (UserUpdateInfoData) and loading state
 */
export const useUserProfileData = (
	accountId: string | null | undefined,
): UseUserProfileDataReturn => {
	const { data, loading } = useQueryInterop<
		useUserProfileDataQuery$data,
		useUserProfileDataQuery$variables
	>(
		graphql`
			query useUserProfileDataQuery($accountIds: [String]) {
				confluenceUsers(accountIds: $accountIds) {
					nodes {
						displayName
						profilePicture {
							path
						}
						... on KnownUser {
							accountId
						}
					}
				}
			}
		`,
		{
			variables: {
				accountIds: accountId ? [accountId] : [],
			},
			skip: !accountId,
		},
	);

	// Extract user data from query response
	const userNode = data?.confluenceUsers?.nodes?.[0];
	let updatedBy: UserUpdateInfoData | undefined;

	if (userNode && userNode.displayName) {
		const accountIdValue = userNode.accountId;
		if (accountIdValue) {
			updatedBy = {
				displayName: userNode.displayName,
				avatarUrl: userNode.profilePicture?.path,
				userId: accountIdValue,
			};
		}
	}

	// On error or missing data, updatedBy remains undefined
	return {
		updatedBy,
		loading,
	};
};
